// ═══════════════════════════════════════════════════════════════
// GeoOps — Dashboard with zone list and incident summary
// ═══════════════════════════════════════════════════════════════

import { useEffect, useMemo } from 'react';
import {
  Title, Text, Paper, Group, Badge, Button, Table, Stack,
  SimpleGrid, Center, Loader, ThemeIcon, Menu,
} from '@mantine/core';
import {
  IconMap2, IconPlus, IconEye, IconTrash, IconAlertTriangle, IconDownload,
  IconFileTypography, IconWorld, IconMap,
} from '@tabler/icons-react';
import { notifications } from '@mantine/notifications';
import { useNavigate } from 'react-router-dom';
import { useAuthStore } from '../../store/authStore';
import { useGeoOpsStore, ZONE_TYPE_META } from '../../store/geoopsStore';
import { useModulePermission } from '../../hooks/useModulePermission';
import { semantic } from '../../lib/colors';
import { exportZonesGeoJSON, exportZonesKML, exportZonesShapefile } from '../../lib/geoopsExport';

export default function GeoOpsDashboard() {
  const navigate = useNavigate();
  const { activeIncident } = useAuthStore();
  const { zones, isLoading, loadZones, deleteZone } = useGeoOpsStore();
  const { canEdit } = useModulePermission('geoops');

  useEffect(() => {
    if (activeIncident) loadZones(String(activeIncident.id));
  }, [activeIncident]);

  const incidentZones = useMemo(
    () => zones.filter(z => String(z.incidentId) === String(activeIncident?.id)),
    [zones, activeIncident]
  );

  const activeZones = incidentZones.filter(z => z.status === 'active');
  const resolvedZones = incidentZones.filter(z => z.status === 'resolved');
  const plannedZones = incidentZones.filter(z => z.status === 'planned');

  if (!activeIncident) {
    return (
      <Center py={80}>
        <Stack align="center" gap="sm">
          <ThemeIcon size={60} radius="xl" variant="light" color="gray">
            <IconAlertTriangle size={30} />
          </ThemeIcon>
          <Title order={3} c="black">No Incident Selected</Title>
          <Text size="sm" c="dimmed">Select an incident to manage geographic zones.</Text>
        </Stack>
      </Center>
    );
  }

  if (isLoading && zones.length === 0) {
    return <Center h={300}><Loader size="md" /></Center>;
  }

  const handleDelete = async (id: number, name: string) => {
    if (!confirm(`Delete zone "${name}"?`)) return;
    await deleteZone(id);
  };

  const safeIncidentName = (activeIncident.name || 'incident').replace(/[^a-z0-9]+/gi, '_').toLowerCase();
  const exportableZones = incidentZones.filter(z => !!z.geometry);

  const handleExportGeoJSON = () => {
    if (exportableZones.length === 0) {
      notifications.show({ message: 'No zones with geometry to export', color: 'yellow' });
      return;
    }
    exportZonesGeoJSON(exportableZones, `${safeIncidentName}_zones.geojson`);
    notifications.show({ message: `Exported ${exportableZones.length} zone${exportableZones.length !== 1 ? 's' : ''} as GeoJSON`, color: 'green' });
  };
  const handleExportKML = () => {
    if (exportableZones.length === 0) {
      notifications.show({ message: 'No zones with geometry to export', color: 'yellow' });
      return;
    }
    exportZonesKML(exportableZones, activeIncident.name || 'Incident', `${safeIncidentName}_zones.kml`);
    notifications.show({ message: `Exported ${exportableZones.length} zone${exportableZones.length !== 1 ? 's' : ''} as KML`, color: 'green' });
  };
  const handleExportShapefile = async () => {
    if (exportableZones.length === 0) {
      notifications.show({ message: 'No zones with geometry to export', color: 'yellow' });
      return;
    }
    try {
      await exportZonesShapefile(exportableZones, `${safeIncidentName}_zones.zip`);
      notifications.show({ message: `Exported ${exportableZones.length} zone${exportableZones.length !== 1 ? 's' : ''} as Shapefile (.zip)`, color: 'green' });
    } catch (err) {
      notifications.show({
        message: err instanceof Error ? err.message : 'Shapefile export failed',
        color: 'red',
      });
    }
  };

  return (
    <div>
      {/* Header */}
      <Group justify="space-between" mb="lg">
        <div>
          <Title order={3} c="black">GeoOps</Title>
          <Text size="sm" c="dimmed">
            {activeIncident.name} — Incident geography & multi-zone management
          </Text>
        </div>
        <Group gap="xs">
          <Button variant="light" color="violet" leftSection={<IconMap2 size={14} />}
            onClick={() => navigate('/geoops/overview')}
            disabled={incidentZones.length === 0}>
            Unified Map (Scan All Zones)
          </Button>
          <Menu shadow="md" position="bottom-end">
            <Menu.Target>
              <Button variant="light" leftSection={<IconDownload size={14} />} disabled={exportableZones.length === 0}>
                Export Zones
              </Button>
            </Menu.Target>
            <Menu.Dropdown>
              <Menu.Label>Download {exportableZones.length} zone{exportableZones.length !== 1 ? 's' : ''}</Menu.Label>
              <Menu.Item leftSection={<IconWorld size={14} />} onClick={handleExportGeoJSON}>
                GeoJSON (.geojson)
              </Menu.Item>
              <Menu.Item leftSection={<IconMap size={14} />} onClick={handleExportKML}>
                KML (.kml) — Google Earth
              </Menu.Item>
              <Menu.Item leftSection={<IconFileTypography size={14} />} onClick={handleExportShapefile}>
                Shapefile (.zip) — ArcGIS / QGIS
              </Menu.Item>
            </Menu.Dropdown>
          </Menu>
          {canEdit && (
            <Button leftSection={<IconPlus size={14} />} onClick={() => navigate('/geoops/map')}>
              Create Zone
            </Button>
          )}
        </Group>
      </Group>

      {/* Stats */}
      <SimpleGrid cols={{ base: 2, sm: 4 }} mb="lg">
        <Paper p="md" withBorder ta="center">
          <Text size="xs" c="dimmed" tt="uppercase" fw={600}>Total Zones</Text>
          <Text fz={28} fw={700}>{incidentZones.length}</Text>
        </Paper>
        <Paper p="md" withBorder ta="center">
          <Text size="xs" c="dimmed" tt="uppercase" fw={600}>Active</Text>
          <Text fz={28} fw={700} c={semantic.success}>{activeZones.length}</Text>
        </Paper>
        <Paper p="md" withBorder ta="center">
          <Text size="xs" c="dimmed" tt="uppercase" fw={600}>Planned</Text>
          <Text fz={28} fw={700} c={semantic.warning}>{plannedZones.length}</Text>
        </Paper>
        <Paper p="md" withBorder ta="center">
          <Text size="xs" c="dimmed" tt="uppercase" fw={600}>Resolved</Text>
          <Text fz={28} fw={700} c="dimmed">{resolvedZones.length}</Text>
        </Paper>
      </SimpleGrid>

      {/* Zone List */}
      {incidentZones.length === 0 ? (
        <Paper p="xl" withBorder>
          <Center py="xl">
            <Stack align="center" gap="sm">
              <IconMap2 size={40} color="#d1d5db" style={{ opacity: 0.3 }} />
              <Text fw={500}>No zones defined</Text>
              <Text size="sm" c="dimmed">
                Open the Map Editor to draw geographic zones for this incident.
              </Text>
              {canEdit && (
                <Button variant="light" size="sm" onClick={() => navigate('/geoops/map')}>
                  Open Map Editor
                </Button>
              )}
            </Stack>
          </Center>
        </Paper>
      ) : (
        <Paper withBorder>
          <Table striped highlightOnHover>
            <Table.Thead>
              <Table.Tr>
                <Table.Th>Zone</Table.Th>
                <Table.Th>Type</Table.Th>
                <Table.Th>Status</Table.Th>
                <Table.Th>Last Updated</Table.Th>
                <Table.Th w={100}>Actions</Table.Th>
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {incidentZones.map(zone => {
                const meta = ZONE_TYPE_META[zone.zoneType] || ZONE_TYPE_META.custom;
                return (
                  <Table.Tr key={zone.id} style={{ cursor: 'pointer' }}
                    onClick={() => navigate(`/geoops/zone/${zone.id}`)}>
                    <Table.Td>
                      <Group gap="xs">
                        <div style={{
                          width: 12, height: 12, borderRadius: 3,
                          backgroundColor: zone.color || meta.color,
                        }} />
                        <Text size="sm" fw={500}>{zone.name}</Text>
                      </Group>
                    </Table.Td>
                    <Table.Td>
                      <Badge size="sm" variant="light" color={meta.mantine}>
                        {meta.label}
                      </Badge>
                    </Table.Td>
                    <Table.Td>
                      <Badge size="sm" variant="dot"
                        color={zone.status === 'active' ? 'green' : zone.status === 'planned' ? 'yellow' : 'gray'}>
                        {zone.status}
                      </Badge>
                    </Table.Td>
                    <Table.Td>
                      <Text size="xs" c="dimmed">
                        {zone.lastUpdated ? new Date(zone.lastUpdated).toLocaleString() : '—'}
                      </Text>
                    </Table.Td>
                    <Table.Td>
                      <Group gap={4}>
                        <Button variant="subtle" size="compact-xs" color="blue"
                          onClick={e => { e.stopPropagation(); navigate(`/geoops/zone/${zone.id}`); }}>
                          <IconEye size={14} />
                        </Button>
                        {canEdit && (
                          <Button variant="subtle" size="compact-xs" color="red"
                            onClick={e => { e.stopPropagation(); handleDelete(zone.id, zone.name); }}>
                            <IconTrash size={14} />
                          </Button>
                        )}
                      </Group>
                    </Table.Td>
                  </Table.Tr>
                );
              })}
            </Table.Tbody>
          </Table>
        </Paper>
      )}
    </div>
  );
}
